import { FC, HTMLAttributes, useEffect, useState, useMemo } from "react";
import clsx from "classnames";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { meGQL, getConfigGQL, achievementsGQL } from "@onmo/graphql/graphql";
import { StaticImage } from "../StaticImage";
import { DailyRewardHeader } from "../DailyRewardHeader";
import { YourRewardCard } from "../YourRewardCard";
import { DailyRewardCategory } from "../DailyRewardCategory";
import { CoinsAnimation } from "../CoinsAnimation";
import Button from "../../core/Button/Button";
import { User } from "../../../models/user";
import { AchievementReward } from "../AchievementReward";
import { PageRoutes } from "../../../constants/historyGaming";
import { DURATION_COIN_ANIMATION } from "../../../constants/animation";
import {
  getDifferentAchievements,
  getAchievementsWithProgress,
} from "../../../models/achievement";
import { LevelUpAnimation } from "../LevelUpAnimation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks";
import { PRODUCT_QUERY } from "../../../constants/achievement";
import { useStyles } from "./RewardScreen.style";
import { useWalletConfig } from "../../../hooks/useWalletConfig";
import { getWalletBalance } from "../../../constants/wallet";
import { assetsImages } from "../../../models/theme";
import { USER_PROFILE_TABS } from "../../../constants/userProfile";

interface IRewardScreenProps {
  rewardAmount: number;
}

export const RewardScreen: FC<
  HTMLAttributes<HTMLDivElement> & IRewardScreenProps
> = (props) => {
  const classes = useStyles();
  const { rewardAmount, className, ...otherProps } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { achievements } = useAppSelector((state) => state.user);
  const { data: cachedData } = useQuery(achievementsGQL, {
    variables: { input: { products: PRODUCT_QUERY } },
    fetchPolicy: "cache-only",
  });

  const [oldAchievements] = useState(() => {
    return cachedData?.me?.achievements?.items || achievements || [];
  });

  const { data: dataMe } = useQuery(meGQL);
  const { data, loading: loadingAchievements } = useQuery(achievementsGQL, {
    variables: {
      input: { products: PRODUCT_QUERY },
    },
    fetchPolicy: "network-only",
  });
  const { data: dataConfig } = useQuery(getConfigGQL);
  const { walletConfig } = useWalletConfig();
  const baseCoinType = walletConfig.primaryCurrency.base;
  const userRank = User.getCurrentRank(
    dataMe?.me?.coinWallets,
    dataConfig?.config.tierLevels,
    baseCoinType,
  );

  const isRankUp = useMemo(() => {
    if (!userRank?.nextLevel || !dataMe) return false;
    return userRank.xp - rewardAmount < userRank.currentLevelXp;
  }, [userRank, rewardAmount, dataMe]);

  const [showAchievement, setShowAchievement] = useState(false);
  const [showCoin, setShowCoin] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [currentAchievementIndex, setCurrentAchievementIndex] = useState(0);

  const newAchievements = data?.me?.achievements?.items;
  const differentAchievements = useMemo(
    () => getDifferentAchievements(oldAchievements, newAchievements ?? []),
    [oldAchievements, newAchievements],
  );
  const achievementsWithProgress = useMemo(
    () =>
      getAchievementsWithProgress(oldAchievements || [], newAchievements ?? []),
    [oldAchievements, newAchievements],
  );

  const onReturnHome = () => {
    navigate(PageRoutes.HomePage, { replace: true });
  };

  const onViewAchievements = () => {
    navigate(`${PageRoutes.Profile}?tab=${USER_PROFILE_TABS.achievements}`, { replace: true });
  };

  const onShowLevelUp = () => {
    const nextIndex = currentAchievementIndex + 1;
    if (nextIndex < differentAchievements.length) {
      setCurrentAchievementIndex(nextIndex);
    } else {
      setShowAchievement(false);
      if (isRankUp) {
        setShowLevelUp(true);
      }
    }
  };

  useEffect(() => {
    if (loadingAchievements) return;

    const timeoutShowCoin = setTimeout(() => {
      setShowCoin(true);
    }, 100);
    const timeoutShowAchievement = setTimeout(() => {
      if (differentAchievements.length > 0) {
        setShowAchievement(true);
        setCurrentAchievementIndex(0);
      } else if (isRankUp) {
        setShowLevelUp(true);
      }
    }, DURATION_COIN_ANIMATION + 100);
    return () => {
      clearTimeout(timeoutShowCoin);
      clearTimeout(timeoutShowAchievement);
    };
  }, [loadingAchievements, differentAchievements.length, isRankUp]);

  const currentBalance = getWalletBalance(
    dataMe?.me?.coinWallets ?? [],
    walletConfig.primaryCurrency.base,
  );

  return (
    <div {...otherProps} className={clsx(classes.root, className)}>
      <StaticImage
        src={assetsImages.rankBG["1x"]}
        src2x={assetsImages.rankBG["2x"]}
        src3x={assetsImages.rankBG["3x"]}
        className={classes.bgImage}
      />
      <div className={classes.content}>
        <DailyRewardHeader
          idCoin="coin-target"
          nbCoin={currentBalance - rewardAmount}
          coinAdd={rewardAmount}
          coinWallets={dataMe?.me?.coinWallets}
        />
        <div className={classes.main}>
          <div className={classes.rewards}>
            <YourRewardCard
              className={classes.rewardCard}
              coinReward={rewardAmount}
            />
            <DailyRewardCategory
              wallets={dataMe?.me?.coinWallets}
              newAchievements={achievementsWithProgress}
              tierLevels={dataConfig?.config.tierLevels}
            />
          </div>
        </div>
        <div className={classes.buttonGroup}>
          <Button fullWidth variant="outlined" onClick={onViewAchievements}>
            {t("common_View Achievements")}
          </Button>
          <Button fullWidth onClick={onReturnHome}>
            {t("common_Return Home")}
          </Button>
        </div>
      </div>
      {showCoin && <CoinsAnimation />}
      {showAchievement &&
        currentAchievementIndex < differentAchievements.length && (
          <AchievementReward
            newAchievement={differentAchievements[currentAchievementIndex]}
            oldAchievements={oldAchievements}
            nbCoin={currentBalance}
            onShowLevelUp={onShowLevelUp}
          />
        )}
      {showLevelUp && isRankUp && <LevelUpAnimation />}
    </div>
  );
};
